import { inject, Injectable } from '@angular/core';
import { map, startWith } from 'rxjs';
import { SurveyService } from './survey.service';
import { GaugeValueKPI } from '../types';
import { ChartType } from '../types/chart-type.types';
import { ChartService } from './chart.service';

const DEFAULT_MAX_VALUE = 100;
const DEFAULT_MIN_VALUE = -100;

enum RTAStates {
  RED = DEFAULT_MIN_VALUE,
  YELLOW = 20,
  GREEN = 50,
}

const THRESHOLDS: GaugeValueKPI['thresholds'] = Object.freeze({
  [RTAStates.RED]: { color: '#D11314', bgOpacity: 0.2 },
  [RTAStates.YELLOW]: { color: '#ffbb2c', bgOpacity: 0.2 },
  [RTAStates.GREEN]: { color: '#3dcc64', bgOpacity: 0.2 },
});

@Injectable()
export class YearNitelNpsChartService extends ChartService<GaugeValueKPI> {
  override readonly chart$: ChartService<GaugeValueKPI>['chart$'];

  constructor() {
    super();
    const npsService = inject(SurveyService);

    const date = new Date();
    const observable = npsService
      .getYearNPS(date.getFullYear())
      .pipe(startWith({ score: null, records: undefined }));

    this.chart$ = observable.pipe(
      map(({ score, records }) => {
        if (score > 0) {
          score = Number(score.toFixed(2));
        }

        return {
          loading: score === null,
          ngClass: [],
          data: {
            type: ChartType.GAUGE,
            id: 'Nitel NPS',
            title: 'Nitel NPS',
            numberLabel: 'NPS',
            value: parseFloat(((score || 0) * 10).toFixed(2)),
            records,
            minValue: DEFAULT_MIN_VALUE,
            maxValue: DEFAULT_MAX_VALUE,
            markers: this.makeMarkers((score || 0) * 10),
            thresholds: { ...THRESHOLDS },
          } satisfies GaugeValueKPI,
        };
      }),
    );
  }

  private makeMarkers(value: number): GaugeValueKPI['markers'] {
    const defaults = Object.freeze({
      color: '#888',
      type: 'line',
      size: 8,
    });

    return {
      [RTAStates.GREEN]: { ...defaults, label: `${RTAStates.GREEN}` },
      [RTAStates.YELLOW]: { ...defaults, label: `${RTAStates.YELLOW}` },
      [RTAStates.RED]: { ...defaults, label: `${RTAStates.RED}` },
      [DEFAULT_MAX_VALUE]: { ...defaults, label: `${DEFAULT_MAX_VALUE}` },
      [value]: {
        ...defaults,
        label: DEFAULT_MAX_VALUE < value ? `${value}` : '',
        type: 'triangle',
        color: '#000',
      },
    };
  }
}
